import React, { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import CustomCursor from './CustomCursor';


function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .islim-root {
    font-family: 'Outfit', sans-serif;
    --orange:       #EA580C;
    --orange-light: #FB923C;
    --orange-dark:  #C2410C;
    --orange-deep:  #7C2D12;
    --amber:        #FDBA74;
    --bg:           #FFF7ED;
    background: var(--bg);
  }
  .islim-root *, .islim-root *::before, .islim-root *::after {
    box-sizing: border-box; font-family: 'Outfit', sans-serif;
  }
  .islim-root h1, .islim-root h2, .islim-root h3,
  .islim-root h4, .islim-root h5, .islim-root h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── HERO ── */
  .hero-section {
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #431407 0%, #9A3412 50%, #7C2D12 100%);
    min-height: 88vh;
    display: flex; align-items: center; justify-content: center;
  }

  .hero-grid { position: absolute; inset: 0; pointer-events: none; perspective: 600px; }
  .hero-grid-inner {
    position: absolute; bottom: -40px; left: -20%; right: -20%; height: 60%;
    background-image:
      linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px);
    background-size: 60px 60px;
    transform: rotateX(70deg); transform-origin: bottom center;
    animation: grid-scroll 8s linear infinite;
    mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  }
  @keyframes grid-scroll { from { background-position: 0 0; } to { background-position: 0 60px; } }

  .hero-orbit {
    position: absolute; inset: -48px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.3);
    animation: orbit3d 8s linear infinite; transform-style: preserve-3d;
  }
  .hero-orbit-2 { inset: -72px; border-color: rgba(253,186,116,0.3); animation: orbit3d 12s linear infinite reverse; }
  @keyframes orbit3d {
    0%   { transform: rotateX(70deg) rotateZ(0deg); }
    100% { transform: rotateX(70deg) rotateZ(360deg); }
  }
  .hero-orbit::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: #fff; border-radius: 50%;
    box-shadow: 0 0 12px 3px rgba(255,255,255,0.9); transform: translateX(-50%);
  }
  .hero-orbit-2::after { background: var(--amber); box-shadow: 0 0 12px 3px rgba(253,186,116,0.9); top: auto; bottom: -4px; }

  .hero-particle { position: absolute; border-radius: 50%; pointer-events: none; animation: particle-float linear infinite; }
  .hero-particle:nth-child(1) { width:4px; height:4px; background:rgba(255,255,255,0.9); left:15%; top:20%; animation-duration:7s; animation-delay:0s; }
  .hero-particle:nth-child(2) { width:3px; height:3px; background:rgba(253,186,116,0.9); left:80%; top:60%; animation-duration:9s; animation-delay:1s; }
  .hero-particle:nth-child(3) { width:5px; height:5px; background:rgba(255,255,255,0.6); left:60%; top:15%; animation-duration:11s; animation-delay:2s; }
  .hero-particle:nth-child(4) { width:3px; height:3px; background:rgba(253,186,116,0.7); left:30%; top:75%; animation-duration:8s; animation-delay:3s; }
  .hero-particle:nth-child(5) { width:4px; height:4px; background:rgba(255,255,255,0.8); left:90%; top:30%; animation-duration:10s; animation-delay:0.5s; }
  @keyframes particle-float {
    0%   { transform: translateY(0) scale(1); opacity: 0; }
    10%  { opacity: 1; } 90% { opacity: 1; }
    100% { transform: translateY(-80px) scale(0.5); opacity: 0; }
  }

  .hero-blob { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; }
  .hero-blob-1 { width:500px; height:500px; background:rgba(194,65,12,0.6); top:-100px; right:-100px; animation:drift 10s ease-in-out infinite alternate; }
  .hero-blob-2 { width:360px; height:360px; background:rgba(124,45,18,0.5); bottom:-80px; left:-60px; animation:drift 13s ease-in-out infinite alternate-reverse; }
  @keyframes drift { from { transform:translate(0,0) scale(1); } to { transform:translate(30px,20px) scale(1.08); } }

  .hero-content {
    position: relative; z-index: 10;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px; max-width: 1200px; width: 100%;
    padding: 120px 60px 80px 80px;
  }

  .hero-image-wrap { flex-shrink: 0; position: relative; }

  .hero-image-glow {
    position: absolute; inset: -24px;
    background: radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%);
    border-radius: 50%; animation: glow-pulse 3s ease-in-out infinite;
  }
  @keyframes glow-pulse { 0%,100%{opacity:0.5;transform:scale(1);} 50%{opacity:1;transform:scale(1.06);} }

  .hero-image-3d {
    transform-style: preserve-3d; transition: transform 0.1s ease;
    cursor: pointer; position: relative;
  }
  .hero-image-3d::after {
    content: ''; position: absolute; inset: 0; border-radius: 24px;
    background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.08) 60%, rgba(255,255,255,0) 100%);
    background-size: 200% 200%; background-position: 200% 200%;
    z-index: 3; pointer-events: none; opacity: 0;
    transition: opacity 0.3s ease, background-position 0.4s ease;
  }
  .hero-image-3d:hover::after { opacity: 1; background-position: -20% -20%; }
  .hero-image-3d:hover .hero-image {
    box-shadow: 0 50px 100px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.5),
      -20px 20px 60px rgba(124,45,18,0.35), 20px -10px 40px rgba(253,186,116,0.35);
  }
  .hero-image-3d::before {
    content: ''; position: absolute; inset: -2px; border-radius: 26px;
    background: linear-gradient(135deg, #fff, var(--amber), var(--orange-light));
    z-index: 1; opacity: 0; transition: opacity 0.3s ease;
  }
  .hero-image-3d:hover::before { opacity: 1; }

  /* ── UPDATED: larger square image ── */
  .hero-image {
    width: 380px; height: 380px; object-fit: cover; border-radius: 24px;
    position: relative; z-index: 2;
    box-shadow: 0 30px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.25), 20px 20px 60px rgba(0,0,0,0.2);
    transition: box-shadow 0.3s ease;
  }

  .hero-image-reflection {
    position: absolute; bottom: -20px; left: 10%; right: 10%; height: 30px;
    background: radial-gradient(ellipse, rgba(255,255,255,0.4) 0%, transparent 70%);
    filter: blur(8px); animation: reflection-pulse 3s ease-in-out infinite;
  }
  @keyframes reflection-pulse { 0%,100%{opacity:0.5;transform:scaleX(0.9);} 50%{opacity:0.9;transform:scaleX(1.05);} }

  .hero-text { flex: 1; padding-left: 40px; }

  .hero-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(255,255,255,0.85); margin-bottom: 16px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.3s;
  }

  .hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(54px,7vw,82px); font-weight: 600; line-height: 1;
    color: white; margin: 0 0 8px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.5s;
  }

  .hero-title-accent {
    display: block; font-family: 'Outfit', sans-serif;
    font-size: clamp(14px,1.8vw,18px); font-weight: 400;
    background: linear-gradient(90deg, #FEF3C7, var(--amber));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; margin-top: 6px;
    letter-spacing: 0.08em; text-transform: uppercase;
  }

  .hero-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, #fff, rgba(255,255,255,0.3));
    margin: 24px 0;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.7s;
  }

  .hero-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 17px; line-height: 1.78; font-weight: 300;
    color: rgba(255,255,255,0.85); max-width: 480px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.9s;
  }

  .back-btn-wrap {
    margin-top: 48px;
    opacity: 0; animation: slideUp 0.8s ease forwards 1.1s;
  }

  .back-products-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.3);
    color: rgba(255,255,255,0.75);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .back-products-btn:hover {
    background: rgba(234,88,12,0.2);
    border-color: rgba(253,186,116,0.6);
    color: var(--amber);
    transform: translateX(-4px);
  }

  .site-link-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(253,186,116,0.45);
    color: var(--amber);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .site-link-btn:hover {
    background: rgba(253,186,116,0.18);
    border-color: var(--amber);
    color: #fff;
    transform: translateX(-4px);
  }

  @keyframes slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }

  /* ── iSLIM IN ACTION (iCoffee-style: left text + right video card) ── */
  .action-section {
    background: linear-gradient(180deg, #2A0A02 0%, #431407 100%);
    padding: 100px 40px;
    position: relative; overflow: hidden;
  }
  .action-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(253,186,116,0.6), transparent);
  }
  .action-section::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(253,186,116,0.3), transparent);
  }

  .action-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px;
  }

  /* Left text block */
  .action-text { flex: 1; min-width: 0; }
  .action-eyebrow {
    font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase; color: var(--amber);
    display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  }
  .action-eyebrow-line { display: inline-block; width: 32px; height: 1px; background: var(--orange-light); }

  .action-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px,5vw,60px); font-weight: 500; line-height: 1.1;
    color: white; margin: 0 0 8px;
  }
  .action-heading-italic {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic; font-weight: 400;
    color: var(--amber); display: block; margin-top: 4px;
  }

  .action-divider {
    width: 48px; height: 1px;
    background: linear-gradient(90deg, var(--orange-light), transparent);
    margin: 24px 0;
  }

  .action-desc {
    font-family: 'Outfit', sans-serif; font-weight: 300;
    font-size: 16px; line-height: 1.8; color: rgba(255,255,255,0.65);
    max-width: 420px; margin-bottom: 32px;
  }

  .action-yt-link {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 12px 26px; border-radius: 100px;
    background: rgba(234,88,12,0.15);
    border: 1px solid rgba(234,88,12,0.45);
    color: var(--amber);
    font-family: 'Outfit', sans-serif; font-size: 0.78rem;
    font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, transform 0.3s, box-shadow 0.3s;
  }
  .action-yt-link:hover {
    background: rgba(234,88,12,0.3);
    border-color: var(--amber);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(124,45,18,0.5);
  }
  .action-yt-icon {
    width: 28px; height: 28px; background: #FF0000; border-radius: 6px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  /* Right: video card */
  .action-video-col { flex-shrink: 0; width: 460px; }

  .action-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(234,88,12,0.28);
    border-radius: 16px; width: 100%; cursor: pointer;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.3s, transform 0.3s;
    overflow: hidden;
  }
  .action-video-card:hover {
    background: rgba(234,88,12,0.12); border-color: rgba(234,88,12,0.6);
    transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(124,45,18,0.45);
  }

  .action-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: rgba(124,45,18,0.4); display: flex; align-items: center; justify-content: center;
  }
  .action-video-thumb img {
    position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.85;
    transition: transform 0.4s ease;
  }
  .action-video-card:hover .action-video-thumb img { transform: scale(1.06); }

  .action-play-overlay {
    position: absolute; inset: 0; background: rgba(67,20,7,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .action-video-card:hover .action-play-overlay { background: rgba(124,45,18,0.6); }

  .action-play-btn {
    width: 56px; height: 56px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; padding-left: 4px; color: var(--orange);
    box-shadow: 0 4px 20px rgba(124,45,18,0.5);
    transition: transform 0.3s, box-shadow 0.3s;
  }
  .action-video-card:hover .action-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(234,88,12,0.6); }

  .action-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }

  .action-card-body { padding: 14px 18px 16px; text-align: left; }
  .action-card-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; margin-bottom: 3px; }
  .action-card-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(253,186,116,0.85); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; line-height: 1.4; }
  .action-card-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── VIDEO MODAL ── */
  .vid-modal-backdrop {
    position: fixed; inset: 0; background: rgba(30,8,2,0.92); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: modal-fade-in 0.25s ease;
  }
  @keyframes modal-fade-in { from{opacity:0;} to{opacity:1;} }
  .vid-modal-box {
    position: relative; width: 100%; max-width: 780px;
    background: #431407; border-radius: 24px;
    border: 1px solid rgba(253,186,116,0.25);
    box-shadow: 0 40px 120px rgba(0,0,0,0.85), 0 0 0 1px rgba(234,88,12,0.15);
    overflow: hidden; animation: modal-slide-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes modal-slide-up { from{opacity:0;transform:scale(0.88) translateY(32px);} to{opacity:1;transform:scale(1) translateY(0);} }
  .vid-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px; border-bottom: 1px solid rgba(253,186,116,0.15);
  }
  .vid-modal-title { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; display: flex; align-items: center; gap: 8px; }
  .vid-modal-lang-badge {
    background: linear-gradient(135deg, var(--orange-dark), var(--orange-light));
    color: white; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
  }
  .vid-modal-close {
    width: 32px; height: 32px; background: rgba(255,255,255,0.1); border: none;
    border-radius: 50%; color: rgba(255,255,255,0.7); font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s; flex-shrink: 0;
  }
  .vid-modal-close:hover { background: rgba(239,68,68,0.3); color: white; }
  .vid-modal-frame-wrap { position: relative; width: 100%; aspect-ratio: 16/9; background: #000; }
  .vid-modal-frame-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .vid-modal-footer { padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .vid-modal-yt-link {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.5); text-decoration: none; transition: color 0.2s;
  }
  .vid-modal-yt-link:hover { color: #FF4444; }
  .vid-modal-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(253,186,116,0.75); letter-spacing: 0.06em; text-transform: uppercase; }

  @media (max-width: 900px) {
    .action-inner { flex-direction: column; gap: 48px; align-items: center; text-align: center; }
    .action-eyebrow { justify-content: center; }
    .action-desc { margin: 0 auto 32px; }
    .action-yt-link { margin: 0 auto; }
    .action-video-col { width: 100%; }
  }

  @media (max-width: 768px) {
    .hero-content { flex-direction: column; gap: 40px; padding: 100px 24px 60px; text-align: center; }
    .hero-text { padding-left: 0; }
    .hero-divider { margin: 24px auto; }
    .hero-desc { margin: 0 auto; }
  }
`;

const ISLIM_VIDEO_ID = 'M8Z1lCTcPE4';

function AnimatedSection({ children }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?'none':'translateY(28px)', transition:'opacity 0.7s ease, transform 0.7s ease' }}>
      {children}
    </div>
  );
}

const ISlim = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMouseMove = (e) => {
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const cx = rect.width / 2, cy = rect.height / 2;
    el.style.transform = `perspective(800px) rotateX(${-((y-cy)/cy)*18}deg) rotateY(${((x-cx)/cx)*18}deg) scale3d(1.06,1.06,1.06)`;
  };

  const handleMouseLeave = () => {
    if (imageRef.current) imageRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)`;
  };

  return (
    <>
      <CustomCursor />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="islim-root min-h-screen flex flex-col">
        <Header />

        {/* ── Hero ── */}
        <section className="hero-section">
          <div className="hero-grid"><div className="hero-grid-inner" /></div>
          <div className="hero-particle" /><div className="hero-particle" /><div className="hero-particle" />
          <div className="hero-particle" /><div className="hero-particle" />
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />

          <div className="hero-content">
            <div className="hero-image-wrap">
              <div className="hero-image-glow" />
              <div className="hero-orbit" />
              <div className="hero-orbit hero-orbit-2" />
              <div className="hero-image-3d" ref={imageRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <img src="/assest/product_islim.jpeg" alt="iSlim" className="hero-image" />
              </div>
              <div className="hero-image-reflection" />
            </div>

            <div className="hero-text">
              <p className="hero-eyebrow">Sustainable Wellness Journey</p>
              <h1 className="hero-title">
                iSlim
                <span className="hero-title-accent">Product Description</span>
              </h1>
              <div className="hero-divider" />
              <p className="hero-desc">
                iSlim is more than just a product — it's your daily companion on a sustainable wellness journey. Powered by our unique FB3™ Fusion, iSlim supports your body's natural rhythm by encouraging minute lifestyle modifications that make a big impact over time. No extreme diets. No harsh restrictions. Just small, mindful changes that help you feel lighter, healthier, and more in control — one day at a time.
              </p>
              <div className="back-btn-wrap" style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <a href="/products" className="back-products-btn">← Back to Products</a>
                <a href="https://vivaislim.com/" target="_blank" rel="noopener noreferrer" className="site-link-btn">
                  Visit iSlim Site ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── iSlim in Action ── */}
        <section className="action-section">
          <div className="action-inner">

            {/* Left: text */}
            <AnimatedSection>
              <div className="action-text">
                <p className="action-eyebrow">
                  <span className="action-eyebrow-line" />
                  YouTube · Watch Now
                </p>
                <h2 className="action-heading">
                  iSlim
                  <span className="action-heading-italic">in Action</span>
                </h2>
                <div className="action-divider" />
                <p className="action-desc">
                  Watch iSlim in action — see how our FB3™ Fusion technology supports your body's natural rhythm and helps you make small, sustainable lifestyle changes that lead to lasting wellness results.
                </p>
                <a
                  href={`https://www.youtube.com/watch?v=${ISLIM_VIDEO_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-yt-link"
                >
                  <span className="action-yt-icon">
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="white">
                      <path d="M5.5 7.5v-5l4.5 2.5-4.5 2.5z"/>
                    </svg>
                  </span>
                  Watch on YouTube
                </a>
              </div>
            </AnimatedSection>

            {/* Right: video card */}
            <AnimatedSection>
              <div className="action-video-col">
                <div
                  className="action-video-card"
                  onClick={() => setActiveVideo({ youtubeId: ISLIM_VIDEO_ID, lang: 'iSlim in Action', topic: 'Weight Wellness · FB3™ Fusion · Lifestyle' })}
                >
                  <div className="action-video-thumb">
                    <img
                      src={`https://i.ytimg.com/vi/${ISLIM_VIDEO_ID}/hqdefault.jpg`}
                      alt="iSlim in Action"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="action-play-overlay">
                      <div className="action-play-btn">▶</div>
                      <span className="action-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="action-card-body">
                    <div className="action-card-name">iSlim — Wellness Journey</div>
                    <div className="action-card-topic">Weight Wellness · FB3™ Fusion · Lifestyle</div>
                    <div className="action-card-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </section>

        {/* ── Video Modal ── */}
        {activeVideo && (
          <div className="vid-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}>
            <div className="vid-modal-box">
              <div className="vid-modal-header">
                <div className="vid-modal-title">
                  <span>iSlim</span>
                  <span className="vid-modal-lang-badge">{activeVideo.lang}</span>
                </div>
                <button className="vid-modal-close" onClick={() => setActiveVideo(null)}>✕</button>
              </div>
              <div className="vid-modal-frame-wrap">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={`iSlim — ${activeVideo.lang}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="vid-modal-footer">
                <span className="vid-modal-topic">{activeVideo.topic}</span>
                <a href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" className="vid-modal-yt-link">
                  <span style={{ fontSize:'14px' }}>▶</span> Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}
      {/* <TestimonyUpload/> */}
        <Footer />
      </div>
    </>
  );
};

export default ISlim;